import { DownloadRecordType } from '@velocity-exchange/common';
import { PublicKey } from '@velocity-exchange/sdk';
import {
	ExportRequestError,
	useRequestExport,
} from 'src/hooks/query/useExportRequests';
import { notify } from 'src/utils/notifications';
import { dlog } from 'src/dev';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

interface Params {
	exportType: DownloadRecordType;
	from: string;
	to: string;
	userKeys: string[];
	accountOptions: { userKey: string; pubKey: PublicKey }[];
	marketSymbol?: string;
	isSubmitDisabled: boolean;
	hide: () => void;
}

export default function useExportHistoryModalSubmit({
	exportType,
	from,
	to,
	userKeys,
	accountOptions,
	marketSymbol,
	isSubmitDisabled,
	hide,
}: Params) {
	const { captureEvent } = usePostHogCapture();
	const { mutateAsync: requestExport, isPending: isSubmitting } =
		useRequestExport();

	const handleSubmit = async () => {
		if (isSubmitDisabled) {
			return;
		}

		const userPublicKeys = userKeys
			.map((userKey) =>
				accountOptions
					.find((acct) => acct.userKey === userKey)
					?.pubKey?.toString()
			)
			.filter((pubKey): pubKey is string => pubKey !== undefined);

		// Bail rather than submit a partial account list — otherwise a selected
		// account that can't be resolved would silently produce an incomplete
		// statement with a success message.
		if (userPublicKeys.length !== userKeys.length) {
			notify({
				type: 'error',
				message: 'Error',
				description:
					'Unable to resolve all selected accounts. Please refresh and try again.',
			});
			return;
		}

		dlog(`downloader`, `requestExport`, {
			fileType: exportType,
			from,
			to,
			userPublicKeys,
			marketSymbol,
		});

		try {
			await requestExport({
				fileType: exportType,
				fromS3Date: from,
				toS3Date: to,
				userPublicKeys,
				marketSymbol,
			});
		} catch (e) {
			const isRateLimited = e instanceof ExportRequestError && e.status === 429;

			notify({
				type: 'error',
				message: 'Error',
				description: isRateLimited
					? 'Too many requests. Please wait before trying again.'
					: 'There was an error requesting the statement(s). Please try again later.',
			});

			console.error(e);
			return;
		}

		captureEvent('pnl_history_exported', {
			statement_type: exportType,
			date_from: from,
			date_to: to,
		});

		notify({
			type: 'info',
			message: 'Generating your statement(s).',
			description:
				"This can take up to 10 minutes. It'll appear in your Exports tab when ready, and you'll be notified if you're still on the app.",
		});

		hide();
	};

	return {
		isSubmitting,
		handleSubmit,
	};
}
